<template>
    <v-dialog v-model="dialog" v-stack="{ open: dialog, close: hide }" :max-width="640" :fullscreen="isMobile" persistent content-class="re-editor-dialog"
        :transition="isMobile ? 'dialog-bottom-transition' : 'dialog-transition'">
        <div class="pfe-panel-inner" :class="{ 'pfe-fullscreen': isMobile }">
            <div class="pfe-header">
                <v-btn icon small class="pfe-back" :class="{ 'pfe-back--close': step === 1 }" @click="onBack">
                    <v-icon size="16">{{ step === 1 ? 'mdi-close' : 'mdi-chevron-left' }}</v-icon>
                </v-btn>
                <span class="pfe-title">{{ title }}</span>
                <history-controls v-if="step === 2 && !loadingEdit" context="editor" />
                <div v-else style="width:32px"></div>
            </div>

            <div class="pfe-steps">
                <div class="pfe-step" :class="{ active: step >= 1 }"></div>
                <div class="pfe-step" :class="{ active: step >= 2 }"></div>
                <div class="pfe-step" :class="{ active: step >= 3 }" v-if="!assignedMode"></div>
            </div>

            <div class="pfe-body">
                <skeleton-loader v-if="loadingEdit" type="rows" :count="5" />

                <!-- PASO 1: INFO -->
                <template v-else-if="step === 1">
                    <kimsa-text-field :label="$t('common.name')"
                        :placeholder="$t('routines.give_routine_name_placeholder')" dense force-value :value="name"
                        @change="name = $event" />
                    <kimsa-combobox :label="$t('common.category')" :placeholder="$t('exercises.pick_one_category')" dense
                        :items="categories" @change="category = $event" :value="category" item-text="label"
                        ref="categories" />
                    <kimsa-tags-picker ref="tagsPicker" @change="tags = $event" :default-tags="tags"
                        :label="$t('exercises.tags')" force-value />
                </template>

                <!-- PASO 2: BLOQUES -->
                <template v-else-if="step === 2">
                    <div class="pfe-section-title">{{ $t('steppers.blocks_and_exercises') }}</div>
                    <div v-if="blocks.length === 0" class="re-empty">{{ $t('routines.no_blocks_created') }}</div>
                    <draggable v-model="blocks" draggable=".pfeb-block" handle=".pfeb-drag" ghost-class="ghost">
                        <routine-block-item v-for="(block, i) in blocks" :key="`${block.uid}-${historyRev}`" :block="block" :index="i"
                            :open="expandedBlocks.includes(block.uid)" @toggle="toggleBlock(block.uid)"
                            @update="onBlockUpdate(i, $event)" @delete="confirmDeleteBlock(i)"
                            @duplicate="duplicateBlock(i)" @change-name="changeBlockName(i)" />
                    </draggable>
                    <div class="re-add-actions">
                        <v-btn text :color="accentColor" class="re-add-block" @click="addBlock">
                            <v-icon left size="16">mdi-plus</v-icon>{{ $t('common.new') }} {{ $tc('common.blocks', 1) }}
                        </v-btn>
                        <v-btn text :color="accentColor" class="re-add-block" @click="$refs.addExistingBlock.show()">
                            <v-icon left size="16">mdi-view-dashboard-outline</v-icon>{{ $t('common.from_library') }}
                        </v-btn>
                    </div>
                </template>

                <!-- PASO 3: ASIGNAR -->
                <template v-else>
                    <div class="re-assign">
                        <div class="re-assign-left">
                            <set-selection-routine :coach-id="coachId" @change="assignSelection = $event"
                                @collection="assignCollection = $event" />
                            <div class="re-assign-warn">
                                <v-icon size="16" color="#D97706">mdi-alert-outline</v-icon>
                                <span>{{ $t('routines.alert_replace_routines') }}</span>
                            </div>
                        </div>
                        <div class="re-assign-right">
                            <div class="re-assign-hint">{{ $t('routines.select_days_to_assign') }}</div>
                            <mini-calendar :value="assignDates" @change="assignDates = $event" />
                        </div>
                    </div>
                </template>

                <kimsa-upload-file v-show="!loadingEdit && step === 1" :label="$t('common.image')"
                    relative-path="images/routines" mime-types="image/*" :preview-img="previewImg" ref="img"
                    height="150" @change="file = $event; deleteImg = false" @delete="onDeleteImg" />
            </div>

            <div class="pfe-footer" :class="{ 'pfe-footer--single': step === 1 }">
                <v-btn v-if="step === 2" text tile class="pfe-btn-cancel" @click="hide">{{ $t('common.cancel') }}</v-btn>
                <v-btn v-if="step === 3" text tile class="pfe-btn-cancel" @click="hide">{{ $t('common.omit') }}</v-btn>
                <v-btn tile depressed color="primary" class="pfe-btn-next black--text" :disabled="nextDisabled"
                    :loading="saving || loading" @click="onNext">
                    {{ nextLabel }}
                </v-btn>
            </div>

            <change-name-dialog ref="name" is-second-dialog @save="onNameSave" />
            <kimsa-confirm ref="confirmDeleteBlock" is-second-dialog alert-mode :title="$t('common.delete_block')"
                :content="$t('common.confirm_deleted_block')" @confirm="doDeleteBlock"
                @cancel="pendingDeleteBlock = null" />
            <add-existing-block-dialog ref="addExistingBlock" is-second-dialog :coach-id="coachId"
                @select="onSelectExistingBlock" />
        </div>
    </v-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { db, Timestamp } from "@/utils/Firebase";
import { assignRoutineToSelection, getMergedBlocks, buildRoutineBlocksMeta } from "@/utils/Exercises";
import { randomString } from "@/utils/Common";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import KimsaTagsPicker from "@/components/common/inputs/KimsaTagsPicker";
import KimsaUploadFile from "@/components/common/inputs/KimsaUploadFile";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import RoutineBlockItem from "@/components/coach/routines/RoutineBlockItem";
import ChangeNameDialog from "@/components/coach/routines/ChangeNameDialog";
import SetSelectionRoutine from "@/components/coach/routines/SetSelectionRoutine";
import MiniCalendar from "@/components/common/MiniCalendar";
import AddExistingBlockDialog from "@/components/coach/routines/AddExistingBlockDialog";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import draggable from "vuedraggable";
import HistoryControls from "@/components/common/HistoryControls";
import editorHistoryMixin from "@/mixins/editorHistoryMixin";

export default {
    name: "RoutineEditor",
    components: {
        KimsaTextField, KimsaCombobox, KimsaTagsPicker, KimsaUploadFile, SkeletonLoader,
        RoutineBlockItem, ChangeNameDialog, SetSelectionRoutine, MiniCalendar, AddExistingBlockDialog, KimsaConfirm, draggable,
        HistoryControls,
    },
    mixins: [editorHistoryMixin],
    props: {
        coachId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            dialog: false,
            step: 1,
            saving: false,
            loading: false,
            loadingEdit: false,
            editMode: false,
            assignedMode: false,
            routineId: null,
            routineRef: null,
            name: '',
            category: null,
            tags: [],
            file: null,
            deleteImg: false,
            blocks: [],
            expandedBlocks: [],
            pendingDeleteBlock: null,
            assignSelection: null,
            assignCollection: null,
            assignDates: [],
        }
    },
    computed: {
        ...mapState(['categoriesOptions']),
        accentColor(vm = this) {
            return vm.$vuetify.theme.dark ? 'primary' : 'accent'
        },
        title(vm = this) {
            if (vm.step === 3) return vm.$t('steppers.assign')
            return vm.editMode ? vm.$t('routines.edit_routine') : vm.$t('routines.create_first_routine')
        },
        nextLabel(vm = this) {
            if (vm.step === 2) return vm.$t('common.save')
            if (vm.step === 3) return vm.$t('common.assign')
            return vm.$t('common.next')
        },
        categories(vm = this) {
            return [...vm.categoriesOptions, { id: 1, key: 'mix', name: 'Mix', value: 'Mix', label: vm.$t('categories.mix') }]
        },
        previewImg(vm = this) {
            return (!vm.deleteImg && vm.routineRef?.img) || vm.file?.img || null
        },
        step1Disabled(vm = this) {
            return !vm.name || !vm.category
        },
        step2Disabled(vm = this) {
            if (vm.blocks.length === 0) return true
            return vm.blocks.some(block => {
                if (block.exercises.length === 0) return true
                return block.exercises.some(ex => ex.type !== 'rest' && !vm.exerciseConfigured(block, ex))
            })
        },
        assignValid(vm = this) {
            const selection = Array.isArray(vm.assignSelection) ? vm.assignSelection : [vm.assignSelection]
            return selection.filter(Boolean).length > 0 && vm.assignDates.length > 0
        },
        nextDisabled(vm = this) {
            if (vm.saving || vm.loading) return true
            if (vm.step === 1) return vm.step1Disabled
            if (vm.step === 2) return vm.step2Disabled
            return !vm.assignValid
        },
        blocksMeta(vm = this) {
            return buildRoutineBlocksMeta(vm.blocks)
        },
        routinePayloadForAssign(vm = this) {
            const meta = vm.blocksMeta
            return {
                id: vm.routineId || '',
                coachId: vm.coachId,
                name: vm.name || '',
                img: vm.routineRef?.img || '',
                imgSavePath: vm.routineRef?.imgSavePath || '',
                description: {
                    equipments: meta.equipments,
                    blocks: vm.blocks.length,
                    objective: vm.category?.name || '',
                    objectiveKey: vm.routineObjectiveKey(vm.category?.name) || ''
                },
                tags: vm.tags || [],
                blocks: meta.blocks
            }
        },
    },
    async mounted() {
        const vm = this
        if (!vm.categoriesOptions || vm.categoriesOptions.length === 0) await vm.fetchExerciseCategories()
    },
    methods: {
        ...mapActions(['fetchExerciseCategories']),
        exerciseConfigured(block, exercise) {
            if (block.isCircuit) {
                if (block.type === 'tabata') return true
                return !!(exercise?.config?.value || exercise?.config?.value === 0)
            }
            return exercise?.config?.sets?.length > 0
        },
        showCreate() {
            const vm = this
            vm.reset()
            vm.editMode = false
            vm.blocks = [{ uid: randomString(), exercises: [] }]
            vm.expandedBlocks = [vm.blocks[0].uid]
            vm.startEditorHistory('blocks')
            vm.show()
        },
        async showEdit(routine) {
            const vm = this
            vm.reset()
            vm.editMode = true
            vm.show()
            await vm.withLoading(async () => {
            const id = routine.objectID || routine.id
            vm.routineId = id
            const snap = await db.collection('routines').doc(id).get()
            if (!snap.exists) {
                vm.toast(vm.$t('common.an_error_has_occurred'), 'error')
                return vm.hide()
            }
            vm.routineRef = snap.data()
            vm.name = vm.routineRef.name || ''
            vm.category = vm.categories.find(c => c.key === vm.routineObjectiveKey(vm.routineRef.description?.objective)) || null
            vm.tags = vm.routineRef.tags || []
            const merged = await getMergedBlocks(vm.routineRef.blocks)
            vm.blocks = merged.map(b => ({ ...b, uid: b.uid || randomString() }))
            vm.expandedBlocks = vm.blocks[0] ? [vm.blocks[0].uid] : []
            if (vm.dialog) vm.startEditorHistory('blocks')
            }, 'loadingEdit')
        },
        async showEditAssigned(routine) {
            const vm = this
            vm.reset()
            vm.editMode = true
            vm.assignedMode = true
            vm.show()
            await vm.withLoading(async () => {
            const id = routine.id || routine.objectID
            vm.routineId = id
            const snap = await db.collection('assignedRoutines').doc(id).get()
            const data = snap.data() || {}
            vm.routineRef = { ...data, id }
            vm.name = data.name || ''
            vm.category = vm.categories.find(c => c.key === vm.routineObjectiveKey(data.description?.objective)) || null
            vm.tags = data.tags || []
            const merged = await getMergedBlocks(data.blocks || [])
            vm.blocks = merged.map(b => ({ ...b, uid: b.uid || randomString() }))
            vm.expandedBlocks = vm.blocks[0] ? [vm.blocks[0].uid] : []
            if (vm.dialog) vm.startEditorHistory('blocks')
            }, 'loadingEdit')
        },
        show() {
            const vm = this
            vm.dialog = true
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.stopEditorHistory()
            vm.reset()
            vm.$emit('close')
        },
        reset() {
            const vm = this
            vm.step = 1
            vm.saving = false
            vm.loading = false
            vm.loadingEdit = false
            vm.editMode = false
            vm.assignedMode = false
            vm.routineId = null
            vm.routineRef = null
            vm.name = ''
            if (vm.$refs.categories) vm.$refs.categories.model = null
            if (vm.$refs.img) vm.$refs.img.reset()
            vm.category = null
            vm.tags = []
            vm.file = null
            vm.deleteImg = false
            vm.blocks = []
            vm.expandedBlocks = []
            vm.assignSelection = null
            vm.assignCollection = null
            vm.assignDates = []
        },
        onDeleteImg() {
            this.deleteImg = true
        },
        onBack() {
            const vm = this
            if (vm.step > 1) return vm.step--
            vm.hide()
        },
        onNext() {
            const vm = this
            if (vm.nextDisabled) return
            if (vm.step === 1) return vm.step = 2
            if (vm.step === 2) return vm.save()
            if (vm.step === 3) return vm.assignRoutine()
        },
        toggleBlock(uid) {
            const vm = this
            const idx = vm.expandedBlocks.indexOf(uid)
            if (idx === -1) vm.expandedBlocks.push(uid)
            else vm.expandedBlocks.splice(idx, 1)
        },
        onBlockUpdate(i, block) {
            this.blocks.splice(i, 1, block)
        },
        addBlock() {
            const vm = this
            const block = { uid: randomString(), exercises: [] }
            vm.blocks.push(block)
            vm.expandedBlocks.push(block.uid)
        },
        async onSelectExistingBlock(hit) {
            const vm = this
            const merged = await getMergedBlocks([hit.block])
            const block = { ...merged[0], name: hit.name || null, uid: randomString() }
            vm.blocks.push(block)
            vm.expandedBlocks.push(block.uid)
        },
        confirmDeleteBlock(i) {
            const vm = this
            vm.pendingDeleteBlock = i
            vm.$refs.confirmDeleteBlock.show()
        },
        doDeleteBlock() {
            const vm = this
            if (vm.pendingDeleteBlock !== null) vm.deleteBlock(vm.pendingDeleteBlock)
            vm.pendingDeleteBlock = null
        },
        deleteBlock(i) {
            const vm = this
            const uid = vm.blocks[i] && vm.blocks[i].uid
            vm.blocks.splice(i, 1)
            const idx = vm.expandedBlocks.indexOf(uid)
            if (idx !== -1) vm.expandedBlocks.splice(idx, 1)
        },
        duplicateBlock(i) {
            const vm = this
            const copy = { ...JSON.parse(JSON.stringify(vm.blocks[i])), uid: randomString() }
            vm.blocks.splice(i + 1, 0, copy)
        },
        changeBlockName(i) {
            this.$refs.name.show(this.blocks[i], i)
        },
        onNameSave(payload) {
            const vm = this
            vm.blocks.splice(payload.index, 1, { ...vm.blocks[payload.index], name: payload.name })
        },
        async save() {
            const vm = this
            await vm.withLoading(async () => {
            let img = null
            if (vm.editMode && vm.routineRef?.img && !vm.deleteImg) img = { tempUrl: vm.routineRef.img, savePath: vm.routineRef.imgSavePath }
            if (vm.file) img = await vm.$refs.img.upload()

            const meta = vm.blocksMeta
            let payload = {
                name: vm.name || '',
                img: img?.tempUrl || img?.img || (vm.deleteImg ? '' : vm.routineRef?.img) || '',
                imgSavePath: img?.savePath || img?.imgSavePath || (vm.deleteImg ? '' : vm.routineRef?.imgSavePath) || '',
                description: {
                    equipments: meta.equipments,
                    blocks: vm.blocks.length,
                    objective: vm.category?.name || '',
                    objectiveKey: vm.routineObjectiveKey(vm.category?.name) || ''
                },
                tags: vm.tags || [],
                blocks: meta.blocks,
            }

            if (vm.assignedMode) {
                delete payload.tags
                payload.blocks = meta.blocks.map((b, i) => ({ ...b, completed: (vm.blocks[i] && vm.blocks[i].completed) || false }))
                if (!await vm.saveAssignedRoutine(vm.routineId, payload)) return vm.hide()
                vm.toast(vm.$t('routines.assigned_routine_edited'))
                vm.$emit('success')
                vm.hide()
                return
            }

            if (vm.editMode) {
                await db.collection('routines').doc(vm.routineId).update(payload)
            } else {
                const ref = await db.collection('routines').add({ ...payload, created_at: Timestamp.now(), coachId: vm.coachId })
                vm.routineId = ref.id
            }
            vm.routineRef = { ...(vm.routineRef || {}), ...payload }
            vm.toast(vm.$t('routines.saved'))
            vm.$emit('success', { objectID: vm.routineId, coachId: vm.coachId, ...payload })
            vm.step = 3
            }, 'saving')
        },
        async assignRoutine() {
            const vm = this
            await vm.withLoading(async () => {
                try {
                    await assignRoutineToSelection({
                        routine: vm.routinePayloadForAssign,
                        collection: vm.assignCollection || 'assignedRoutines',
                        selection: vm.assignSelection,
                        dates: vm.assignDates,
                    })
                    vm.toast(vm.$t('routines.assigned_routines_successfully'))
                    vm.$emit('success')
                    vm.hide()
                } catch (err) {
                    if (err?.code !== 'already-exists') vm.toast(vm.$t('common.an_error_has_occurred'), 'error')
                }
            }, 'loading')
        },
    },
}
</script>

<style scoped>
.pfe-panel-inner {
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 16px;
    overflow: hidden;
    width: 100%;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
}

.pfe-fullscreen {
    height: 100%;
    max-height: none;
    border-radius: 0;
    border: none;
}

.pfe-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    flex-shrink: 0;
    background: var(--v-surface-base);
    border-bottom: 1px solid var(--v-outline-base);
}

.pfe-fullscreen .pfe-header {
    padding-top: calc(14px + var(--safe-area-inset-top, 0px));
}

.pfe-title {
    font-size: 15px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.pfe-back {
    background: var(--v-surface2-base) !important;
    color: var(--v-txtSecondary-base) !important;
}

.pfe-back .v-icon {
    color: inherit !important;
}

.pfe-back--close {
    background: rgba(232, 92, 92, 0.12) !important;
    color: var(--v-error-base) !important;
}

.pfe-steps {
    display: flex;
    gap: 6px;
    padding: 12px 16px;
    background: var(--v-surface-base);
    flex-shrink: 0;
}

.pfe-step {
    flex: 1;
    height: 3px;
    border-radius: 2px;
    background: var(--v-outline-base);
}

.theme--dark .pfe-step.active {
    background: var(--v-primary-base);
}

.theme--light .pfe-step.active {
    background: var(--v-accent-base);
}

.pfe-body {
    flex: 1;
    overflow-y: auto;
    padding: 20px 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

@media (max-width: 959px) {
    .pfe-body::-webkit-scrollbar {
        display: none;
    }
}

.pfe-section-title {
    font-size: 15px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.re-empty {
    text-align: center;
    font-size: 13px;
    color: var(--v-txtMuted-base);
    padding: 20px 0;
}

.re-add-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
}

.re-add-block {
    text-transform: none !important;
    letter-spacing: normal !important;
    border: 1px dashed var(--v-outline-base) !important;
    border-radius: 12px !important;
    height: 46px !important;
}

@media (max-width: 600px) {
    .re-add-actions {
        grid-template-columns: 1fr;
    }
}

.ghost {
    opacity: 0.5;
}

.re-assign {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
}

.re-assign-left {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.re-assign-right {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.re-assign-hint {
    font-size: 11px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
    text-transform: uppercase;
    letter-spacing: .06em;
    text-align: center;
    margin-bottom: 8px;
}

.re-assign-warn {
    margin-top: auto;
    display: flex;
    gap: 10px;
    align-items: flex-start;
    background: rgba(217, 119, 6, .08);
    border: 1px solid rgba(217, 119, 6, .35);
    border-radius: 8px;
    padding: 12px 14px;
    font-size: 12px;
    color: #D97706;
    line-height: 1.5;
}

@media (max-width: 768px) {
    .re-assign {
        grid-template-columns: 1fr;
        gap: 16px;
    }
}

.pfe-footer {
    display: grid;
    grid-template-columns: 1fr 2fr;
    border-top: 1px solid var(--v-outline-base);
    flex-shrink: 0;
    overflow: hidden;
}

.pfe-footer--single {
    grid-template-columns: 1fr;
}

.pfe-fullscreen .pfe-footer>.v-btn {
    height: calc(52px + var(--safe-area-inset-bottom, 0px)) !important;
    padding-bottom: var(--safe-area-inset-bottom, 0px);
}

.pfe-footer>.v-btn {
    width: 100% !important;
    min-width: 0 !important;
    height: 52px !important;
    border-radius: 0 !important;
    text-transform: none !important;
    letter-spacing: normal !important;
}

.pfe-btn-cancel {
    background: var(--v-surface2-base) !important;
    border-right: 1px solid var(--v-outline-base);
    color: var(--v-txtSecondary-base) !important;
    font-size: 14px;
    font-weight: 600;
}

.pfe-btn-next {
    font-size: 15px;
    font-weight: 700;
}
</style>
